'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useFocusRing } from 'react-aria';
import { TONE } from '../system/tone';
import { interactiveBg, useInteractive } from '../system/interactive';
import { MOTION, focusRing } from '../system/motion';
const CARD_PAD_VAR = {
    flush: 0,
    compact: 'var(--space-lg)',
    default: 'var(--space-xl)',
    spacious: 'var(--space-2xl)',
};
function resolvePad(pad) {
    if (pad in CARD_PAD_VAR)
        return CARD_PAD_VAR[pad];
    return `var(--space-${pad})`;
}
export function Card(props) {
    return props.onClick ? _jsx(ClickableCard, { ...props }) : _jsx(StaticCard, { ...props });
}
function baseStyle({ p, tone, accentBorder, bordered, inset, interactive, hover, active, showFocusRing, direction, clip, flush, extra, }) {
    const background = tone
        ? TONE[tone].bg
        : interactive
            ? interactiveBg({ hover, active, focus: false, focusForced: false, disabled: false }, 'var(--surface-module-bg)')
            : 'var(--surface-module-bg)';
    // Boxed-surface treatments:
    //   • default (neutral module) → soft `--surface-module-shadow` AND a faint
    //     `--surface-module-border` hairline. The universal top-level boxed unit.
    //   • tone / inset → a 1px hairline, no shadow. Tinted callouts separate by
    //     colour (a shadow on a pale tint reads as heavy and regresses the "tint
    //     fades into the page" fix); nested embeds must not stack a second
    //     shadow. Both read as outlined sub-elements.
    //   • accentBorder → a 1px stroke in the tone colour, no shadow.
    //   • bordered={false} → bare: no border, no shadow, for a card hosted inside
    //     another framing surface.
    let border = 'none';
    let boxShadow = 'none';
    if (bordered === false) {
        // bare — no frame at all
    }
    else if (accentBorder) {
        border = `1px solid ${TONE[accentBorder].bg}`;
    }
    else if (tone || inset) {
        border = '1px solid var(--border)';
    }
    else {
        // Module surface: the soft shadow AND a faint hairline outline together.
        border = 'var(--surface-module-border)';
        boxShadow = 'var(--surface-module-shadow)';
    }
    const shouldClip = clip ?? (direction === 'column' || flush);
    return {
        background,
        color: tone ? TONE[tone].ink : 'var(--foreground)',
        border,
        borderRadius: 'var(--surface-module-radius)',
        padding: p,
        // Interactive cards show the pointer. Non-interactive cards leave the
        // cursor unset so the browser's natural per-element cursor applies — the
        // text I-beam over copy, the default arrow over empty card chrome. Forcing
        // `default` here previously suppressed the I-beam over selectable text.
        cursor: interactive ? 'pointer' : undefined,
        transition: interactive ? MOTION.transition : 'none',
        // Hover swaps only the background (white → neutral) via `interactiveBg`;
        // the module shadow is constant, so there is no elevation change on hover.
        boxShadow,
        ...(direction ? { display: 'flex', flexDirection: direction } : {}),
        ...(shouldClip ? { overflow: 'hidden' } : {}),
        ...focusRing(showFocusRing),
        ...extra,
    };
}
function StaticCard({ pad = 'default', tone, accentBorder, bordered = true, inset = false, interactive: interactiveProp, direction, clip, children, style, className, id, ref, }) {
    const p = resolvePad(pad);
    const flush = pad === 'flush';
    const interactive = !!interactiveProp;
    const { state, bind } = useInteractive({});
    return (_jsx("div", { ref: ref, id: id, className: className, style: baseStyle({
            p, tone, accentBorder, bordered, inset, interactive,
            hover: state.hover, active: state.active,
            showFocusRing: false, direction, clip, flush, extra: style,
        }), ...(interactive ? bind : {}), children: children }));
}
function ClickableCard({ pad = 'default', tone, accentBorder, bordered = true, inset = false, onClick, direction, clip, children, style, className, id, ref, }) {
    const p = resolvePad(pad);
    const flush = pad === 'flush';
    const { state, bind } = useInteractive({ onClick });
    const { isFocusVisible, focusProps } = useFocusRing();
    return (_jsx("div", { ref: ref, id: id, className: className, tabIndex: 0, role: "button", style: baseStyle({
            p, tone, accentBorder, bordered, inset, interactive: true,
            hover: state.hover, active: state.active,
            showFocusRing: isFocusVisible, direction, clip, flush, extra: style,
        }), ...bind, ...focusProps, children: children }));
}
//# sourceMappingURL=Card.js.map